/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link } from "../components/ui/Link";
import { Info } from "lucide-react";
import {
  listFaxes,
  FAX_VERDICT_LABELS,
  type FaxStatus,
  type FaxSummary,
} from "../api/faxes";
import { useChrome } from "../contexts/ChromeContext";
import { EmptyState } from "../components/ui/EmptyState";
import { Badge } from "../components/ui/Badge";
import { Select } from "../components/ui/Select";
import { Button } from "../components/ui/Button";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Spinner } from "../components/ui/Spinner";
import { FaxIcon } from "../components/ui/FaxIcon";
import { Tooltip } from "../components/ui/Tooltip";

const FILTER_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "ALL", label: "All faxes" },
];

/**
 * Inbound referral faxes: what Healthie received, what the pipeline extracted, and whether a human
 * still needs to act. Pending cases are the work queue; resolved ones are the audit trail.
 */
export function ReferralFaxes() {
  const [status, setStatus] = useState<FaxStatus | null>("PENDING");
  const [items, setItems] = useState<FaxSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useChrome({}, []);

  const load = useCallback(
    async (filter: FaxStatus | null, pageToLoad: number) => {
      const result = await listFaxes(filter, pageToLoad);
      if (!result.ok) {
        setError(result.error.message);
        return;
      }
      setError(null);
      setTotal(result.value.total);
      setHasMore(result.value.hasMore);
      setItems((previous) =>
        pageToLoad === 0
          ? result.value.items
          : [...(previous ?? []), ...result.value.items],
      );
    },
    [],
  );

  useEffect(() => {
    setItems(null);
    setPage(0);
    void load(status, 0);
  }, [status, load]);

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Fax inbox
        </h1>
        <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[15px] leading-relaxed text-text-secondary">
          <span>
            Open a fax to review it and create its referral, or mark it
            resolved.
          </span>
          <Tooltip
            content="Faxes received since Aug 21, 2026, when fax intake moved into the console. Earlier faxes were turned into referrals automatically and are listed under Referrals."
            className="max-w-xs whitespace-normal text-left"
          >
            <Info
              size={15}
              aria-label="About this inbox"
              className="text-text-tertiary"
            />
          </Tooltip>
        </p>
      </header>

      <div className="enter flex flex-wrap items-center gap-3">
        <Select
          className="w-full sm:w-56"
          value={status ?? "ALL"}
          onChange={(value) =>
            setStatus(value === "ALL" ? null : (value as FaxStatus))
          }
          options={FILTER_OPTIONS}
        />
      </div>

      <ErrorMessage message={error} />

      {items === null && error === null ? (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      ) : items !== null && items.length === 0 ? (
        <EmptyState
          icon={<FaxIcon size={20} />}
          tone={status === null ? "quiet" : "search"}
          title={
            status === "RESOLVED"
              ? "No resolved faxes"
              : status === "PENDING"
                ? "No faxes waiting"
                : "No faxes yet"
          }
          description={
            status === "RESOLVED"
              ? "Faxes you create a referral from or mark resolved show here."
              : "New faxes arrive from Healthie and land here for review."
          }
          actionLabel={status === null ? undefined : "Show all faxes"}
          onAction={status === null ? undefined : () => setStatus(null)}
        />
      ) : items !== null ? (
        <>
          <p className="text-sm text-text-tertiary" aria-live="polite">
            {total === 1 ? "1 fax" : `${total} faxes`}
          </p>
          <ol className="space-y-2.5">
            {items.map((fax, index) => (
              <FaxRow key={fax.id} fax={fax} index={index} />
            ))}
          </ol>
          {hasMore && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  const next = page + 1;
                  setPage(next);
                  void load(status, next);
                }}
              >
                Load more
              </Button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function FaxRow({ fax, index }: { fax: FaxSummary; index: number }) {
  const received = fax.receivedAt ?? fax.createdAt;
  return (
    <li
      className="enter"
      style={
        { "--enter-delay": `${Math.min(index, 12) * 35}ms` } as CSSProperties
      }
    >
      <Link
        to={`/referrals/faxes/${fax.id}`}
        className="border border-border bg-bg-elevated shadow-card hover-raise flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl px-5 py-4"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-medium text-text-primary">
            {fax.fromNumber ?? "Unknown sender"}
          </p>
          <p className="mt-0.5 text-[13px] text-text-tertiary">
            {received
              ? new Date(received).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })
              : "Received date unknown"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {fax.intakeFailed && <Badge tone="error">Intake failed</Badge>}
          {fax.organizationVerdict && (
            <Badge tone="neutral">
              Org:{" "}
              {FAX_VERDICT_LABELS[fax.organizationVerdict] ??
                fax.organizationVerdict}
            </Badge>
          )}
          {fax.providerVerdict && (
            <Badge tone="neutral">
              Provider:{" "}
              {FAX_VERDICT_LABELS[fax.providerVerdict] ?? fax.providerVerdict}
            </Badge>
          )}
          {fax.confidence !== null && (
            <Badge tone={fax.confidence > 0.9 ? "success" : "warning"}>
              {Math.round(fax.confidence * 100)}%
            </Badge>
          )}
          <Badge tone={fax.status === "PENDING" ? "warning" : "success"}>
            {fax.status === "PENDING" ? "Pending" : "Resolved"}
          </Badge>
        </div>
      </Link>
    </li>
  );
}
